import { useState, useEffect, useCallback, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { Star, Check, X, Loader2, ChevronDown, ChevronUp, MessageSquare } from "lucide-react";

interface ReviewRow {
  readonly id: string;
  readonly productId: string;
  readonly productName: string | null;
  readonly customerName: string;
  readonly rating: number;
  readonly title: string | null;
  readonly body: string | null;
  readonly status: string;
  readonly source: string;
  readonly createdAt: string;
}

interface ReviewListResponse {
  readonly success: boolean;
  readonly data: readonly ReviewRow[];
  readonly meta: {
    readonly total: number;
    readonly page: number;
    readonly limit: number;
    readonly pendingCount: number;
  };
}

type StatusFilter = "all" | "pending" | "approved" | "rejected";

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  pending:  { label: "ממתין לאישור", className: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30" },
  approved: { label: "מאושר",        className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
  rejected: { label: "נדחה",         className: "bg-destructive/15 text-destructive border-destructive/30" },
};

function Stars({ count }: { readonly count: number }): ReactNode {
  return (
    <span className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={cn(
            "h-3.5 w-3.5",
            i < count ? "fill-yellow-400 text-yellow-400" : "fill-muted text-muted"
          )}
        />
      ))}
    </span>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function ReviewListPage(): ReactNode {
  const { accessToken } = useAuth();
  const [reviews, setReviews] = useState<readonly ReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const limit = 20;

  const apiHeaders = useCallback(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (filter !== "all") params.set("status", filter);
      const res = await fetch(`/api/reviews/admin?${params.toString()}`, { headers: apiHeaders() });
      const json = (await res.json()) as ReviewListResponse;
      if (json.success) {
        setReviews(json.data);
        setTotal(json.meta.total);
        setPendingCount(json.meta.pendingCount);
      }
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [page, filter, apiHeaders]);

  useEffect(() => { void fetchReviews(); }, [fetchReviews]);

  const updateStatus = useCallback(async (id: string, status: "approved" | "rejected") => {
    try {
      await fetch(`/api/reviews/admin/${id}/status`, {
        method: "PUT",
        headers: apiHeaders(),
        body: JSON.stringify({ status }),
      });
      await fetchReviews();
    } catch {
      // non-critical
    }
  }, [apiHeaders, fetchReviews]);

  const handleBulkAction = useCallback(async (status: "approved" | "rejected") => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      await fetch("/api/reviews/admin/bulk-status", {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({ ids: Array.from(selectedIds), status }),
      });
      setSelectedIds(new Set());
      await fetchReviews();
    } catch {
      // non-critical
    } finally {
      setBulkLoading(false);
    }
  }, [selectedIds, apiHeaders, fetchReviews]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const totalPages = Math.ceil(total / limit);

  const FILTER_TABS: readonly { value: StatusFilter; label: string }[] = [
    { value: "all",      label: "הכל" },
    { value: "pending",  label: `ממתינות${pendingCount > 0 ? ` (${pendingCount})` : ""}` },
    { value: "approved", label: "מאושרות" },
    { value: "rejected", label: "נדחות" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">ביקורות</h1>
          {total > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">{total} ביקורות סה"כ</p>
          )}
        </div>
        {pendingCount > 0 && (
          <span className="inline-flex items-center gap-1.5 bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30 rounded-full px-3 py-1 text-sm font-semibold">
            <span className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />
            {pendingCount} ממתינות לאישור
          </span>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTER_TABS.map((tab) => (
          <Button
            key={tab.value}
            variant={filter === tab.value ? "default" : "outline"}
            size="sm"
            className={cn(
              "min-h-[38px] shrink-0",
              tab.value === "pending" && pendingCount > 0 && filter !== "pending" && "border-yellow-500/50 text-yellow-600 dark:text-yellow-400"
            )}
            onClick={() => { setFilter(tab.value); setPage(1); }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-primary/5 border border-primary/20">
          <span className="text-sm font-medium text-primary">{selectedIds.size} נבחרו</span>
          <div className="h-4 w-px bg-border" />
          <Button
            size="sm"
            className="min-h-[38px] gap-1.5"
            disabled={bulkLoading}
            onClick={() => void handleBulkAction("approved")}
          >
            {bulkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            אשר הכל
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="min-h-[38px] gap-1.5"
            disabled={bulkLoading}
            onClick={() => void handleBulkAction("rejected")}
          >
            <X className="h-3.5 w-3.5" />
            דחה הכל
          </Button>
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : reviews.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-medium">אין ביקורות</p>
              <p className="text-sm text-muted-foreground mt-1">ביקורות לקוחות יופיעו כאן</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => {
            const statusInfo = STATUS_MAP[review.status];
            const isExpanded = expandedId === review.id;
            const isPending = review.status === "pending";
            const isSelected = selectedIds.has(review.id);

            return (
              <Card
                key={review.id}
                className={cn(
                  "shadow-sm transition-colors",
                  isPending && "border-yellow-500/30",
                  isSelected && "border-primary/40 bg-primary/5"
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Checkbox — pending only */}
                    {isPending && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(review.id)}
                        className="mt-1 h-4 w-4 rounded shrink-0"
                      />
                    )}

                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Top row */}
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Stars count={review.rating} />
                          <span className="font-semibold text-sm">{review.customerName}</span>
                          {review.source === "google" && (
                            <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                              Google
                            </span>
                          )}
                        </div>
                        {statusInfo && (
                          <span className={cn(
                            "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold shrink-0",
                            statusInfo.className
                          )}>
                            {statusInfo.label}
                          </span>
                        )}
                      </div>

                      {/* Meta */}
                      <p className="text-xs text-muted-foreground">
                        {review.productName ?? "מוצר לא ידוע"} &middot; {formatDate(review.createdAt)}
                      </p>

                      {/* Title */}
                      {review.title && (
                        <p className="font-semibold text-sm">{review.title}</p>
                      )}

                      {/* Body — expandable */}
                      {review.body && (
                        <div>
                          {isExpanded ? (
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                              {review.body}
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {review.body}
                            </p>
                          )}
                          {review.body.length > 120 && (
                            <button
                              type="button"
                              onClick={() => setExpandedId(isExpanded ? null : review.id)}
                              className="mt-1 text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              {isExpanded ? (
                                <><ChevronUp className="h-3 w-3" /> הצג פחות</>
                              ) : (
                                <><ChevronDown className="h-3 w-3" /> הצג עוד</>
                              )}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Action buttons — pending only */}
                      {isPending && (
                        <div className="flex gap-2 pt-1">
                          <Button
                            size="sm"
                            className="min-h-[44px] gap-1.5"
                            onClick={() => void updateStatus(review.id, "approved")}
                          >
                            <Check className="h-3.5 w-3.5" />
                            אשר
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="min-h-[44px] gap-1.5"
                            onClick={() => void updateStatus(review.id, "rejected")}
                          >
                            <X className="h-3.5 w-3.5" />
                            דחה
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="min-h-[38px]"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            הקודם
          </Button>
          <span className="flex items-center text-sm text-muted-foreground px-2">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="min-h-[38px]"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            הבא
          </Button>
        </div>
      )}
    </div>
  );
}
